import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiSearch, FiLogOut, FiBell } from 'react-icons/fi';
import { useAuthContext } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { notificationAPI } from '../services/api';
import logger from '../utils/logger';
import { playNotificationSoundDouble, playNotificationWithBrowserAPI } from '../utils/sound';

const Navbar = ({ hideAuthButtons = false, hideNav = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Use auth context — reactive, updates on login/logout without page reload
  const { user, isAuthenticated, logout } = useAuthContext();

  // Initialize audio context once on first page interaction (required for Web Audio API)
  useEffect(() => {
    const initAudioContext = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') {
          ctx.resume().then(() => {

          }).catch(() => {});
        }
      } catch (error) {

      }
    };

    // Initialize on first interaction
    const handleInteraction = () => {
      initAudioContext();
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };

    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('keydown', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  // Use socket context for real-time notifications
  const { 
    notifications, 
    setNotifications, 
    unreadCount, 
    setUnreadCount 
  } = useSocket();

  // Initial load of notifications
  useEffect(() => {
    if (isAuthenticated) {
      loadInitialNotifications();
    }
  }, [isAuthenticated]);

  const loadInitialNotifications = async () => {
    try {
      const response = await notificationAPI.getNotifications();
      if (response && response.success && Array.isArray(response.notifications)) {
        setNotifications(response.notifications);
        const unread = response.notifications.filter(n => !n.read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      logger.error('Failed to load initial notifications', { error: error.message });
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      logger.error('Failed to mark notification as read', { error: error.message });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      logger.error('Failed to mark all notifications as read', { error: error.message });
    }
  };

  const userInitials = user
    ? user.fullName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : '';

  const handleLogout = () => {
    try {
      logger.info('User initiated logout');
      logout(); // clears context state + localStorage atomically
      setIsProfileOpen(false);
      navigate('/');
    } catch (error) {
      logger.error('Logout failed', { error: error.message });
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <nav className="bg-gray-950 border-b border-gray-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition min-w-fit">
            <div className="w-8 h-8 bg-red-500 rounded flex items-center justify-center">
              <span className="text-white font-bold text-xs">PC</span>
            </div>
            <span className="text-white text-lg font-bold">PLAYCONNECT</span>
          </Link>

          {/* Desktop Menu */}
          {!hideNav && isAuthenticated && !['/login', '/register'].includes(location.pathname) && (
            <div className="hidden lg:flex items-center space-x-8 flex-1 justify-center">
              <Link
                to="/"
                className={`transition text-sm font-medium ${
                  location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/matches'
                    ? 'text-red-500 border-b-2 border-red-500'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                HOME
              </Link>
              <Link
                to="/discover"
                className={`transition text-sm font-medium ${
                  location.pathname === '/discover'
                    ? 'text-red-500 border-b-2 border-red-500'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                DISCOVER
              </Link>
              <Link
                to="/create-match"
                className={`transition text-sm font-medium ${
                  location.pathname === '/create-match'
                    ? 'text-red-500 border-b-2 border-red-500'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                CREATE MATCH
              </Link>
              <Link
                to="/my-matches"
                className={`transition text-sm font-medium ${
                  location.pathname === '/my-matches'
                    ? 'text-red-500 border-b-2 border-red-500'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                MY MATCHES
              </Link>
            </div>
          )}


          {/* Search Bar */}
          {!hideNav && isAuthenticated && !['/login', '/register'].includes(location.pathname) && (
            <div className="hidden md:flex items-center flex-1 max-w-xs mx-4">

              <form onSubmit={handleSearchSubmit} className="relative w-full">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="SEARCH..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 text-xs font-medium focus:outline-none focus:border-red-500 transition rounded"
                />
              </form>
            </div>
          )}

          {/* Right Section */}
          <div className="hidden md:flex items-center space-x-4 min-w-fit">
            {!hideAuthButtons && !isAuthenticated && (
              <>
                <Link to="/login" className="text-red-500 font-semibold hover:text-red-400 transition text-sm">
                  Login
                </Link>
                <Link to="/register" className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded text-sm transition">
                  Register
                </Link>
              </>
            )}

            {!hideNav && isAuthenticated && (
              <div className="flex items-center gap-4 relative">
                {/* Notifications Bell */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setIsNotificationsOpen(!isNotificationsOpen);
                      setIsProfileOpen(false);
                    }}
                    className="text-gray-300 hover:text-white transition relative"
                  >
                    <FiBell size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                        {unreadCount}
                      </span>
                    )}
                  </button>

                  {isNotificationsOpen && (
                    <div className="absolute right-0 mt-4 w-80 bg-gray-900 border border-gray-800 rounded shadow-lg z-50 max-h-96 overflow-y-auto">
                      <div className="flex justify-between items-center p-3 border-b border-gray-800">
                        <h3 className="text-white font-bold text-sm">Notifications</h3>
                        {unreadCount > 0 && (
                          <button onClick={handleMarkAllAsRead} className="text-red-500 text-xs hover:text-red-400">
                            Mark all read
                          </button>
                        )}
                      </div>
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">No notifications</div>
                      ) : (
                        notifications.map(notif => (
                          <div 
                            key={notif._id} 
                            onClick={() => {
                              if (!notif.read) handleMarkAsRead(notif._id);
                              if (notif.match) navigate(`/match/${notif.match._id || notif.match}`);
                              setIsNotificationsOpen(false);
                            }}
                            className={`p-3 border-b border-gray-800 hover:bg-gray-800 cursor-pointer transition ${notif.read ? 'opacity-70' : 'bg-gray-800/50'}`}
                          >
                            <p className="text-gray-300 text-xs mb-1">{notif.message}</p>
                            <p className="text-gray-500 text-[10px]">{new Date(notif.createdAt).toLocaleDateString()}</p>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Profile Avatar */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setIsProfileOpen(!isProfileOpen);
                      setIsNotificationsOpen(false);
                    }}
                    className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded font-semibold flex items-center justify-center transition cursor-pointer overflow-hidden"
                  >
                    {user?.profileImage ? (
                      <img src={user.profileImage} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      userInitials
                    )}
                  </button>
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-gray-900 border border-gray-800 rounded shadow-lg z-50">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800 transition"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800 transition flex items-center gap-2"
                    >
                      <FiLogOut size={16} /> Logout
                    </button>
                  </div>
                )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white"
          >
            {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-4 border-t border-gray-800">
            {!hideNav && isAuthenticated && !['/login', '/register'].includes(location.pathname) && (
              <>
                <Link to="/" className="block py-2 text-gray-300 hover:text-white text-sm font-medium">
                  HOME
                </Link>
                <Link to="/discover" className="block py-2 text-gray-300 hover:text-white text-sm font-medium">
                  DISCOVER
                </Link>
                <Link to="/create-match" className="block py-2 text-gray-300 hover:text-white text-sm font-medium">
                  CREATE MATCH
                </Link>
                <Link to="/my-matches" className="block py-2 text-gray-300 hover:text-white text-sm font-medium">
                  MY MATCHES
                </Link>
              </>
            )}

            <div className="mt-4 space-y-2">
              {!hideNav && isAuthenticated && !['/login', '/register'].includes(location.pathname) && (
                <>
                  <Link to="/profile" className="block py-2 text-gray-300 hover:text-white text-sm font-medium">
                    PROFILE
                  </Link>
                  <form onSubmit={handleSearchSubmit} className="relative mt-2">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="SEARCH..."
                      className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 text-xs font-medium focus:outline-none focus:border-red-500 transition rounded"
                    />
                  </form>
                  <div className="pt-4 mt-2 border-t border-gray-800">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider">Notifications</h3>
                      {unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                          {unreadCount} New
                        </span>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto mb-4 bg-gray-900 rounded border border-gray-800">
                      {notifications.length === 0 ? (
                        <div className="p-3 text-center text-gray-500 text-xs">No notifications</div>
                      ) : (
                        notifications.map(notif => (
                          <div 
                            key={notif._id} 
                            onClick={() => {
                              if (!notif.read) handleMarkAsRead(notif._id);
                              if (notif.match) navigate(`/match/${notif.match._id || notif.match}`);
                              setIsOpen(false);
                            }}
                            className={`p-3 border-b border-gray-800 hover:bg-gray-800 cursor-pointer transition ${notif.read ? 'opacity-70' : 'bg-gray-800/50'}`}
                          >
                            <p className="text-gray-300 text-xs mb-1">{notif.message}</p>
                            <p className="text-gray-500 text-[10px]">{new Date(notif.createdAt).toLocaleDateString()}</p>
                          </div>
                        ))
                      )}
                    </div>
                    <button 
                      onClick={handleLogout}
                      className="w-full flex items-center justify-center gap-2 bg-gray-800 hover:bg-red-500 hover:text-white text-gray-300 font-medium py-2 rounded text-sm transition"
                    >
                      <FiLogOut size={16} /> Logout
                    </button>
                  </div>
                </>
              )}

              {!hideAuthButtons && !isAuthenticated && (
                <>
                  <Link to="/login" className="block text-red-500 font-semibold hover:text-red-400 text-sm">
                    Login
                  </Link>
                  <Link to="/register" className="block bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded text-sm text-center transition">
                    Register
                  </Link>
                </>
              )}

            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
